import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import { ArrowRight, Check, ClipboardCheck, MessageCircle, Sparkles, Wand2 } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'

import { OrderComposer } from '@/components/OrderComposer'
import { type PhoneBubble, PhoneMock } from '@/components/PhoneMock'
import { ScreenHeader } from '@/components/ScreenHeader'
import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { useApp } from '@/context'
import type { Client, Comanda, Mesaj } from '@/db/dexie'
import { getClienti, getComanda, getMesaje } from '@/db/queries'
import { fmtKg, fmtOra, fmtRON } from '@/lib/format'
import { cn } from '@/lib/utils'

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return (words[0]?.slice(0, 2) ?? '?').toUpperCase()
  return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase()
}

const STATUS_META: Record<
  Mesaj['status'],
  { label: string; className: string; dot: 'amber' | 'pine' | 'muted' }
> = {
  nou: { label: 'Nou', className: 'bg-amber-soft text-amber-ink', dot: 'amber' },
  transformat: { label: 'Comandă', className: 'bg-pine-soft text-pine', dot: 'pine' },
  citit: { label: 'Citit', className: 'bg-muted text-muted-foreground', dot: 'muted' },
}

export default function Mesaje() {
  const mesaje = useLiveQuery(() => getMesaje(), [])
  const clienti = useLiveQuery(getClienti, [], [] as Client[])
  const clientById = new Map(clienti.map((c) => [c.id, c]))

  const [sel, setSel] = useState<string | null>(null)
  const [composerOpen, setComposerOpen] = useState(false)
  const [composerMsgId, setComposerMsgId] = useState<string | null>(null)
  const [revealing, setRevealing] = useState(false)

  // default the selection to the star message (msg-1), else first unread
  useEffect(() => {
    if (!mesaje) return
    if (sel && mesaje.some((m) => m.id === sel)) return
    const star = mesaje.find((m) => m.id === 'msg-1')
    const nou = mesaje.find((m) => m.status === 'nou')
    setSel(star?.id ?? nou?.id ?? mesaje[0]?.id ?? null)
  }, [mesaje, sel])

  const selectedMsg = mesaje?.find((m) => m.id === sel) ?? null
  const selectedClient = selectedMsg?.clientId ? clientById.get(selectedMsg.clientId) : undefined

  // typing → reveal beat when a "nou" message is opened
  useEffect(() => {
    if (!selectedMsg) return
    if (selectedMsg.status === 'nou') {
      setRevealing(true)
      const t = setTimeout(() => setRevealing(false), 950)
      return () => clearTimeout(t)
    }
    setRevealing(false)
  }, [selectedMsg?.id, selectedMsg?.status])

  const linkedComanda = useLiveQuery<Comanda | undefined>(
    () => (selectedMsg?.comandaId ? getComanda(selectedMsg.comandaId) : Promise.resolve(undefined)),
    [selectedMsg?.comandaId],
  )

  const nume = (m: Mesaj) => clientById.get(m.clientId ?? '')?.nume ?? m.numeAfisat ?? m.telefon
  const unread = mesaje?.filter((m) => m.status === 'nou').length ?? 0

  const bubbles: PhoneBubble[] = (() => {
    if (!selectedMsg || revealing) return []
    const out: PhoneBubble[] = [
      {
        id: `${selectedMsg.id}-in`,
        side: 'in',
        text: selectedMsg.text,
        ora: fmtOra(selectedMsg.ora),
      },
    ]
    if (selectedMsg.status === 'transformat') {
      out.push({
        id: `${selectedMsg.id}-out`,
        side: 'out',
        text: 'Am notat comanda, o pregătim. Mulțumim! 🐔',
        ora: fmtOra(selectedMsg.ora),
        ticks: true,
      })
    }
    return out
  })()

  function openComposer(id: string) {
    setComposerMsgId(id)
    setComposerOpen(true)
  }

  return (
    <div>
      <ScreenHeader tab="mesaje">
        {unread > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-soft px-3 py-1 text-sm font-medium text-amber-ink">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-amber" />
            </span>
            {unread} de transformat
          </span>
        )}
      </ScreenHeader>

      <Explainer />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
        {/* inbox */}
        <div className="min-w-0 rounded-xl border border-border bg-card shadow-paper">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="font-display text-lg font-medium">Mesaje primite</h2>
            <span className="text-xs text-muted-foreground nums">
              {mesaje?.length ?? 0} conversații
            </span>
          </div>
          <div className="flex flex-col p-1.5 lg:max-h-[608px] lg:overflow-y-auto">
            {mesaje?.map((m, i) => {
              const meta = STATUS_META[m.status]
              const active = m.id === sel
              return (
                <motion.button
                  key={m.id}
                  type="button"
                  onClick={() => setSel(m.id)}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                  className={cn(
                    'relative flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                    active ? 'bg-amber-soft/55' : 'hover:bg-secondary',
                  )}
                >
                  {active && (
                    <motion.span
                      layoutId="inbox-active"
                      className="absolute top-2 bottom-2 left-0 w-1 rounded-full bg-amber"
                    />
                  )}
                  <div
                    className={cn(
                      'flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                      m.status === 'nou'
                        ? 'bg-amber text-amber-ink'
                        : 'bg-secondary text-muted-foreground',
                    )}
                  >
                    {initials(nume(m))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className={cn(
                          'truncate text-sm',
                          m.status === 'nou' ? 'font-semibold' : 'font-medium',
                        )}
                      >
                        {nume(m)}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground nums">
                        {fmtOra(m.ora)}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{m.text}</p>
                    <span
                      className={cn(
                        'mt-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium',
                        meta.className,
                      )}
                    >
                      {meta.dot === 'pine' ? (
                        <Check className="size-3" />
                      ) : (
                        <span
                          className={cn(
                            'size-1.5 rounded-full',
                            meta.dot === 'amber' ? 'bg-amber' : 'bg-taupe',
                          )}
                        />
                      )}
                      {meta.label}
                    </span>
                  </div>
                </motion.button>
              )
            })}
            {mesaje && mesaje.length === 0 && (
              <p className="px-3 py-10 text-center text-sm text-muted-foreground">
                Niciun mesaj deocamdată.
              </p>
            )}
          </div>
        </div>

        {/* phone + action */}
        <div className="flex flex-col items-center gap-4 lg:items-stretch">
          {selectedMsg ? (
            <PhoneMock
              nume={nume(selectedMsg)}
              subtitle={selectedMsg.status === 'nou' ? 'online' : 'ultima dată azi'}
              bubbles={bubbles}
              typing={revealing}
              convKey={`${sel}:${revealing}`}
            />
          ) : (
            <div className="flex h-[520px] w-[340px] max-w-full items-center justify-center rounded-[2.25rem] border border-dashed border-border text-sm text-muted-foreground">
              Selectează o conversație
            </div>
          )}

          {selectedMsg && (
            <ActionPanel
              mesaj={selectedMsg}
              client={selectedClient}
              linked={linkedComanda}
              onTransform={() => openComposer(selectedMsg.id)}
            />
          )}
        </div>
      </div>

      <OrderComposer open={composerOpen} onOpenChange={setComposerOpen} mesajId={composerMsgId} />
    </div>
  )
}

function ActionPanel({
  mesaj,
  client,
  linked,
  onTransform,
}: {
  mesaj: Mesaj
  client?: Client
  linked: Comanda | undefined
  onTransform: () => void
}) {
  const { openOn } = useApp()

  if (mesaj.status === 'nou') {
    return (
      <div className="w-[340px] max-w-full rounded-xl border border-amber/30 bg-card p-4 shadow-paper amber-wash">
        {client && (
          <p className="mb-2.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="flex size-4 items-center justify-center rounded-full bg-pine text-pine-foreground">
              <Check className="size-2.5" />
            </span>
            Client recunoscut: <span className="font-medium text-foreground">{client.nume}</span>
          </p>
        )}
        <Button className="w-full" size="lg" onClick={onTransform} data-tour="btn-transforma">
          <Wand2 className="size-4" />
          Transformă în comandă
        </Button>
        <p className="mt-2 text-center text-[11px] leading-snug text-muted-foreground">
          Citim mesajul, recunoaștem produsele și cantitățile, aplicăm prețurile.
        </p>
      </div>
    )
  }

  if (mesaj.status === 'transformat' && linked) {
    return (
      <div className="w-[340px] max-w-full rounded-xl border border-pine/25 bg-card p-4 shadow-paper">
        <div className="mb-2.5 flex items-center gap-1.5 text-sm font-medium text-pine">
          <span className="flex size-5 items-center justify-center rounded-full bg-pine text-pine-foreground">
            <Check className="size-3.5" />
          </span>
          Deja transformat în comandă
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
          <div>
            <div className="font-mono text-sm font-semibold text-amber-ink">{linked.id}</div>
            <div className="mt-0.5 text-xs text-muted-foreground nums">
              {fmtKg(linked.totalKg)} · {fmtRON(linked.totalRON)}
            </div>
          </div>
          <StatusBadge status={linked.status} />
        </div>
        <Button
          variant="outline"
          className="mt-3 w-full"
          onClick={() => openOn('comenzi', linked.id)}
        >
          Vezi în Comenzi
          <ArrowRight className="size-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="w-[340px] max-w-full rounded-xl border border-border bg-card p-4 text-center text-sm text-muted-foreground shadow-paper">
      Mesaj citit — nu conține o comandă de procesat.
    </div>
  )
}

function Explainer() {
  const steps = [
    {
      icon: MessageCircle,
      titlu: 'Mesaj WhatsApp',
      text: 'Text liber, scris în grabă, cu prescurtări și greșeli.',
    },
    {
      icon: Wand2,
      titlu: 'Recunoaștere pe reguli',
      text: 'Potrivim fiecare rând cu produsele și cantitățile tale.',
    },
    {
      icon: ClipboardCheck,
      titlu: 'Comandă prețuită',
      text: 'Structurată și cu prețuri — fără caiet, fără greșeli.',
    },
  ]
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="mb-5 overflow-hidden rounded-xl border border-border bg-card shadow-paper amber-wash"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Sparkles className="size-4 text-amber-ink" />
        <p className="text-sm">
          <span className="font-medium">Cum funcționează:</span>{' '}
          <span className="text-muted-foreground">
            mesajul de pe WhatsApp devine comandă structurată și prețuită în câteva secunde.
          </span>
        </p>
      </div>
      <div className="grid gap-3 p-4 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-center">
        {steps.map((s, i) => (
          <Fragment key={s.titlu}>
            {i > 0 && (
              <div className="hidden items-center justify-center text-muted-foreground/50 sm:flex">
                <ArrowRight className="size-4" />
              </div>
            )}
            <div className="flex items-start gap-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-ink">
                <s.icon className="size-4" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium leading-tight">{s.titlu}</div>
                <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{s.text}</p>
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </motion.div>
  )
}
